/**
 * Beer styles — BJCP 2021 beer guidelines (categories 1–34).
 *
 * The style bars in the recipe editor read straight off {@link styleSchema}'s
 * `vitals`. Guideline *text* is not reproduced: every `overallImpression` here
 * is written by us (PLAN §6, licensing note), and the numeric vitals are
 * facts.
 */

import { z } from 'zod';
import { provenanceSchema, range, slugSchema, tagsSchema } from './common';

/**
 * Which guideline set the entry belongs to.
 *
 * Only BJCP 2021 ships now; the field exists so Brewers Association styles can
 * be added later without an id collision or a breaking type change.
 */
/**
 * Which guideline a style comes from.
 *
 * `bjcp-2021-provisional` is the BJCP's own mechanism for styles that exist
 * between guideline editions: numbered X1, X2 and so on, independent of the
 * 34 numbered categories, and liable to be folded into a category — or
 * dropped — when the next edition lands. They are separated here rather than
 * mixed in because a provisional style has no category and no sub-style
 * letter, and because a brewer entering one should know its definition is not
 * settled.
 */
export const styleGuidelineSchema = z.enum(['bjcp-2021', 'bjcp-2021-provisional']);

/** @see styleGuidelineSchema */
export type StyleGuideline = z.infer<typeof styleGuidelineSchema>;

/**
 * The numeric envelope of a style.
 *
 * Every field is nullable: specialty and "base style + X" categories (27–34)
 * genuinely have no published vitals, and an honest `null` beats a fabricated
 * range that would then be drawn as a style bar.
 */
export const styleVitalsSchema = z.strictObject({
  /** Original gravity, SG. */
  og: range(1.0, 1.16).nullable(),
  /** Final gravity, SG. */
  fg: range(0.98, 1.06).nullable(),
  /** Bitterness, IBU. */
  ibu: range(0, 120).nullable(),
  /** Colour, SRM. */
  srm: range(0, 50).nullable(),
  /** Alcohol by volume, %. */
  abv: range(0, 25).nullable(),
});

/** @see styleVitalsSchema */
export type StyleVitals = z.infer<typeof styleVitalsSchema>;

/** One BJCP sub-style. */
export const styleSchema = z.strictObject({
  /** Stable slug, e.g. `bjcp-2021-21a`. */
  id: slugSchema,
  /** @see styleGuidelineSchema */
  guideline: styleGuidelineSchema,
  /** Category number, 1–34. */
  categoryNumber: z.number().int().min(1).max(34),
  /**
   * Sub-style letter within the category, `A`–`H`, or `null`.
   *
   * `null` only for provisional styles, which are numbered rather than
   * lettered — X5 is X5, not 5A. A null letter is therefore the marker that a
   * row is provisional, and {@link styleCode} reads it that way.
   */
  categoryLetter: z
    .string()
    .regex(/^[A-H]$/)
    .nullable(),
  /** Sub-style name, e.g. `American IPA`. */
  name: z.string().min(3).max(60),
  /** Category name, e.g. `IPA`. */
  categoryName: z.string().min(3).max(60),
  /** @see styleVitalsSchema */
  vitals: styleVitalsSchema,
  /** Two or three sentences in our own words. Never BJCP prose. */
  overallImpression: z.string().min(60).max(600),
  /** Commercial beers, names only — no descriptions, no tasting notes. */
  commercialExamples: z.array(z.string().min(2).max(60)).max(10),
  /** Search/filter tags: `lager`, `hoppy`, `sour`, `german`. */
  tags: tagsSchema,
  /** @see provenanceSchema */
  provenance: provenanceSchema,
});

/** @see styleSchema */
export type Style = z.infer<typeof styleSchema>;

/** The human-facing code for a style, e.g. `21A`. */
export function styleCode(style: Pick<Style, 'categoryNumber' | 'categoryLetter'>): string {
  // A null letter means a provisional style, which BJCP number as X1, X2 and
  // so on — `5` with no letter would collide with category 5's sub-styles.
  if (style.categoryLetter === null) return `X${style.categoryNumber}`;
  return `${style.categoryNumber}${style.categoryLetter}`;
}
